import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  Link,
  Chip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
  Article as ArticleIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getPublicationById, getPublicationMentions } from '../../services/adminApi';
import MonitoringUsersDialog from '../../components/admin/MonitoringUsersDialog';
import RetryPublicationButton from '../../components/admin/Publications/RetryPublicationButton';
import ParsingHistoryPanel from '../../components/admin/Publications/ParsingHistoryPanel';
import type {
  PublicationDetailDto,
  MentionDetailDto,
} from '../../types/publication';

const PublicationDetailPage: React.FC = () => {
  const { publicationId } = useParams<{ publicationId: string }>();
  const { t, i18n } = useTranslation('admin');
  const navigate = useNavigate();
  const { token } = useAuth();

  const [publication, setPublication] = useState<PublicationDetailDto | null>(null);
  const [mentions, setMentions] = useState<MentionDetailDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalMentions, setTotalMentions] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Monitoring users dialog state
  const [monitoringDialogOpen, setMonitoringDialogOpen] = useState(false);
  const [selectedNationalIdId, setSelectedNationalIdId] = useState<string | null>(null);
  const [selectedNationalId, setSelectedNationalId] = useState<string | null>(null);

  // Load publication details
  const loadPublication = useCallback(async () => {
    if (!token || !publicationId) return;

    try {
      setLoading(true);
      setError(null);

      const data = await getPublicationById(parseInt(publicationId), token);
      setPublication(data);
    } catch (err: any) {
      console.error('Failed to load publication:', err);
      if (err.response?.status === 404) {
        setError(t('publications.detail.errors.notFound'));
      } else {
        setError(t('publications.detail.errors.loadFailed'));
      }
      setPublication(null);
    } finally {
      setLoading(false);
    }
  }, [publicationId, token, t]);

  // Load mentions
  const loadMentions = useCallback(async () => {
    if (!token || !publicationId) return;

    try {
      setMentionsLoading(true);

      const result = await getPublicationMentions(
        parseInt(publicationId),
        paginationModel.page + 1, // DataGrid uses 0-based, API uses 1-based
        paginationModel.pageSize,
        token
      );

      setMentions(result.items);
      setTotalMentions(result.totalCount);
    } catch (err: any) {
      console.error('Failed to load mentions:', err);
      setMentions([]);
      setTotalMentions(0);
    } finally {
      setMentionsLoading(false);
    }
  }, [publicationId, paginationModel, token]);

  useEffect(() => {
    loadPublication();
  }, [loadPublication]);

  useEffect(() => {
    if (publication) {
      loadMentions();
    }
  }, [loadMentions, publication]);

  const handleBackToList = () => {
    navigate(`/${i18n.language}/admin/publications`);
  };

  const handleViewMonitors = (nationalIdId: string, normalizedId: string) => {
    setSelectedNationalIdId(nationalIdId);
    setSelectedNationalId(normalizedId);
    setMonitoringDialogOpen(true);
  };

  const handleCloseMonitoringDialog = () => {
    setMonitoringDialogOpen(false);
    setSelectedNationalIdId(null);
    setSelectedNationalId(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'normalizedId',
      headerName: t('publications.detail.mentions.columns.nationalId'),
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleViewMonitors(params.row.nationalIdId, params.row.normalizedId)}
          sx={{ textDecoration: 'none' }}
        >
          {params.row.normalizedId}
        </Link>
      ),
    },
    {
      field: 'fullName',
      headerName: t('publications.detail.mentions.columns.fullName'),
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => params.value || '—',
    },
    {
      field: 'matchedText',
      headerName: t('publications.detail.mentions.columns.matchedText'),
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'matchType',
      headerName: t('publications.detail.mentions.columns.matchType'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={t(`publications.detail.mentions.matchTypes.${params.value}`) || params.value}
          size="small"
          color={params.value === 'Exact' ? 'success' : params.value === 'Normalized' ? 'primary' : 'default'}
        />
      ),
    },
    {
      field: 'pageNumber',
      headerName: t('publications.detail.mentions.columns.pageNumber'),
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => params.value || '—',
    },
    {
      field: 'context',
      headerName: t('publications.detail.mentions.columns.context'),
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => params.value || '—',
    },
    {
      field: 'createdAt',
      headerName: t('publications.detail.mentions.columns.createdAt'),
      width: 150,
      valueFormatter: (value: string) =>
        new Date(value).toLocaleDateString(i18n.language),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error || !publication) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('publications.detail.errors.loadFailed')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
        >
          {t('publications.detail.backToList')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            sx={{ mb: 1 }}
          >
            {t('publications.detail.backToList')}
          </Button>
          <Typography variant="h4" gutterBottom>
            {t('publications.detail.title')}
          </Typography>
        </Box>
        {publicationId && (
          <RetryPublicationButton
            publicationId={parseInt(publicationId)}
            onRetryAccepted={loadPublication}
          />
        )}
      </Box>

      {/* Publication Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ArticleIcon sx={{ mr: 1 }} />
            {t('publications.detail.information.title')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" gutterBottom>
                {publication.title}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {t('publications.detail.information.source')}
              </Typography>
              <Typography variant="body1">
                {publication.source}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {t('publications.detail.information.publicationDate')}
              </Typography>
              <Typography variant="body1">
                {new Date(publication.publicationDate).toLocaleDateString(i18n.language)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {t('publications.detail.information.referenceId')}
              </Typography>
              <Typography variant="body1">
                {publication.referenceId || '—'}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {t('publications.detail.information.mentionCount')}
              </Typography>
              <Typography variant="body1">
                <Chip label={publication.mentionCount} color="primary" size="small" />
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {t('publications.detail.information.createdAt')}
              </Typography>
              <Typography variant="body1">
                {new Date(publication.createdAt).toLocaleDateString(i18n.language)}
              </Typography>
            </Grid>

            {publication.sourceUrl && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('publications.detail.information.url')}
                </Typography>
                <Box>
                  <Link href={publication.sourceUrl} target="_blank" rel="noopener noreferrer">
                    <Button startIcon={<OpenInNewIcon />} size="small" sx={{ mt: 0.5 }}>
                      {t('publications.detail.information.openSource')}
                    </Button>
                  </Link>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Parsing History Timeline */}
      {publicationId && (
        <Box sx={{ mb: 3 }}>
          <ParsingHistoryPanel publicationId={parseInt(publicationId)} />
        </Box>
      )}

      {/* Mentions Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ViewIcon sx={{ mr: 1 }} />
          {t('publications.detail.mentions.title')} ({totalMentions})
        </Typography>

        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={mentions}
            columns={columns}
            loading={mentionsLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalMentions}
            paginationMode="server"
            localeText={{
              noRowsLabel: t('publications.detail.mentions.empty'),
            }}
          />
        </Paper>
      </Paper>

      {/* Monitoring Users Dialog */}
      <MonitoringUsersDialog
        open={monitoringDialogOpen}
        onClose={handleCloseMonitoringDialog}
        nationalIdId={selectedNationalIdId}
        nationalId={selectedNationalId}
      />
    </Box>
  );
};

export default PublicationDetailPage;
